/**
 * Reads the team's "Daily Update" Word document (.docx) into days of work logs and project logs.
 * Ported unchanged from the original app; the matching rules are tuned to that document's wording.
 * Unzipping is done by the caller (JSZip) — this module only reads `word/document.xml` text.
 */
import type { Member, ParsedDay, ParsedDayBucket, Project } from '@/types/models';
import { today } from './format';

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** "25 September 2026" / "25th September" → ISO date; a date without a year that would be in the future is taken as last year. */
export function parseDocDate(txt: string): string | null {
  const m = txt.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)(?:\s+(\d{4}))?/);
  if (!m) return null;
  const mi = MONTHS.indexOf(m[2].toLowerCase());
  if (mi < 0) return null;
  const y = m[3] ? Number(m[3]) : new Date().getFullYear();
  let iso = `${y}-${String(mi + 1).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
  if (!m[3] && iso > today()) iso = `${y - 1}-${iso.slice(5)}`;
  return iso;
}

const toHours = (n: string, unit: string) => (/min/i.test(unit) ? Math.round((Number(n) / 60) * 100) / 100 : Number(n));

const splitNames = (str: string) =>
  str
    .replace(/\s+from the [^,]*?team/i, '')
    .split(/\s*,\s*(?:and\s+)?|\s+and\s+/i)
    .map((x) => x.trim())
    .filter((x) => x && /^[A-Z][\w.'-]*(?:\s[A-Z][\w.'-]*)?$/.test(x));

export interface DocParagraph {
  t: string;
  /** styled as a heading */
  h: boolean;
}

/** Paragraph texts (and whether each is a heading) from `word/document.xml`. */
export function docxParagraphs(xml: string): DocParagraph[] {
  const out: DocParagraph[] = [];
  const re = /<w:p[\s>][\s\S]*?<\/w:p>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    const para = m[0];
    const t = (para.match(/<w:t[^>]*>[^<]*<\/w:t>/g) || [])
      .map((x) => x.replace(/<[^>]+>/g, ''))
      .join('')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .trim();
    const st = (para.match(/<w:pStyle w:val="([^"]+)"/) || [])[1] || '';
    out.push({ t, h: /heading/i.test(st) });
  }
  return out;
}

/** Turns paragraphs into days. Known project and member names help recognise headings and people. */
export function parseDailyDoc(paras: DocParagraph[], ctx: { projects: Pick<Project, 'name'>[]; members: Pick<Member, 'name'>[] }): ParsedDay[] {
  const days: (Omit<ParsedDay, 'date'> & { date: string | null })[] = [];
  let day: (typeof days)[number] | null = null;
  let proj: string | null = null;
  let prefix = '';
  let teamHours: number | null = null;
  const projNames = ctx.projects.map((p) => p.name.toLowerCase());
  const isProjectHeading = (pp: DocParagraph) => pp.h || projNames.includes(pp.t.toLowerCase()) || /^[A-Z][\w-]+\.com$/i.test(pp.t);
  const empty = (): ParsedDayBucket => ({ logs: [], metrics: [], notes: [] });
  const bucket = (): ParsedDayBucket => {
    if (!proj) return day!.general;
    if (!day!.projects[proj]) day!.projects[proj] = empty();
    return day!.projects[proj];
  };
  const NOISE = /^(the\s+)?(projects?|this|it|that)$/i;
  const cleanWork = (w: string | undefined) => {
    const v = (w || '').replace(/^the\s+/i, '').replace(/[.;,]+$/, '').trim();
    return NOISE.test(v) ? '' : v;
  };
  const knownMember = (str: string) => {
    const first = str.trim().toLowerCase();
    return ctx.members.find((x) => x.name.toLowerCase() === first || x.name.split(' ')[0].toLowerCase() === first);
  };
  const cleanNum = (v: string) => {
    const s = String(v).replace(/,/g, '').replace(/\.$/, '');
    return /^(nil|none|n\/a)$/i.test(s) ? '0' : s;
  };
  for (const pp of paras) {
    let t = pp.t;
    if (!t) continue;
    if (/^daily update\b/i.test(t)) {
      const d = parseDocDate(t.replace(/^daily update\s*-?\s*/i, ''));
      day = { date: d, general: empty(), projects: {} };
      days.push(day);
      proj = null;
      prefix = '';
      teamHours = null;
      continue;
    }
    if (!day) continue;
    if (isProjectHeading(pp)) {
      proj = t.replace(/:$/, '');
      prefix = '';
      teamHours = null;
      bucket();
      continue;
    }
    const b = bucket();
    t = t.replace(/^today,\s*/i, '');
    // "The <name> Team worked for 8 hours." → hours applied to the following per-person lines
    if (/^the\s+.+\s+team worked for/i.test(t)) {
      const tm = t.match(/(\d+(?:\.\d+)?)\s*(hours?|minutes?)/i);
      if (tm) teamHours = toHours(tm[1], tm[2]);
      continue;
    }
    // "X[, Y and Z] [from the … Team] worked [on …] for [around] N hours [on|to …]"
    const wre =
      /([A-Z][\w.'-]*(?:(?:,\s*and\s+|,\s*|\s+and\s+)[A-Z][\w.'-]*)*)(?:\s+from the [^,]*?Team)?\s+worked(?:\s+on\s+(.+?))?\s+for\s+(?:around\s+)?(\d+(?:\.\d+)?)\s*(hours?|hrs?|minutes?|mins?)(?:\s+(?:on|to)\s+([^.;]+?))?\s*(?=[.,;]|\s+and\s+[A-Z]|$)/g;
    let m: RegExpExecArray | null;
    let hit = false;
    while ((m = wre.exec(t))) {
      hit = true;
      const names = splitNames(m[1]);
      const work = cleanWork(m[5] || m[2]);
      const hours = toHours(m[3], m[4]);
      names.forEach((n) => b.logs.push({ name: n, hours, work }));
    }
    if (hit) continue;
    // Prose with counts ("12 are currently in progress", "2/3 people have passed" …) → numbers
    let got = false;
    const lead = (t.match(/^([A-Z][\w ]{1,30}):\s/) || [])[1];
    let r: RegExpMatchArray | null;
    if ((r = t.match(/(\d+)\s+are currently in progress/i))) {
      b.metrics.push({ label: (lead || 'Items') + ' in progress', value: r[1] });
      got = true;
    }
    if ((r = t.match(/(\d+|no)\s+new\s+(?:person|people)\s+(?:has|have)?\s*completed/i)) || (r = t.match(/(\d+|no)\s+(?:person|people)\s+(?:has|have)\s+completed/i))) {
      b.metrics.push({ label: (lead || 'Items') + ' completed', value: /no/i.test(r[1]) ? '0' : r[1] });
      got = true;
    }
    if ((r = t.match(/(\d+)\/(\d+)\s+people have passed/i))) {
      b.metrics.push({ label: 'Tests taken', value: r[2] });
      b.metrics.push({ label: 'Tests passed', value: r[1] });
      got = true;
    }
    if ((r = t.match(/with\s+(\d+)\s+total attempts/i))) {
      b.metrics.push({ label: 'Total test attempts', value: r[1] });
      got = true;
    }
    if ((r = t.match(/(\d+)\s+certificates?\s+(?:have|has)\s+been generated/i))) {
      b.metrics.push({ label: 'Certificates generated', value: r[1] });
      got = true;
    }
    if (got) continue;
    // section / company headers
    if (/:$/.test(t) || (t.length < 40 && !/[.!?]$/.test(t) && t.split(' ').length <= 4 && !t.includes(':'))) {
      const h = t.replace(/:$/, '').trim();
      prefix = /data|overview|platform|task|result|notes|repeat|summary/i.test(h) ? '' : h;
      continue;
    }
    // per-person work without "worked": "Name: 3/3 items…" or "Name created/uploaded 10/10 items…"
    const clauses = t.split(/\s*\/\s+(?=[A-Z][a-z]+\s+(?:created|completed|handled|did|reviewed|uploaded))/);
    let personHit = false;
    for (const c of clauses) {
      const km = c.match(/^([A-Z][\w.'-]*(?:\s[A-Z][\w.'-]*)?)(?::\s*|\s+(?=(?:created|completed|handled|did|reviewed|uploaded|conducted|prepared|worked)\b))(.+)$/);
      const STOP =
        /^(total|data|coverage|notes?|overall|platform|verification|new|active|daily|average|unique|audit|acknowledgement|overdue|sop|pkt|ai|repeat|repeated|goals|reports|tasks|to do|in progress|induction|learning|policies|tests|certificates|companies|sessions|employees)\b/i;
      const person = km && !STOP.test(km[1]) && (knownMember(km[1]) || (teamHours != null && /^[A-Z][a-z]+(?:\s[A-Z][a-z]+)?$/.test(km[1])));
      if (km && person && !/^[-+\d.,%]+\.?$/.test(km[2].trim())) {
        b.logs.push({ name: (knownMember(km[1]) || { name: km[1] }).name, hours: teamHours ?? 0, work: km[2].replace(/\.$/, '').trim() });
        personHit = true;
      }
    }
    if (personHit) continue;
    // "Label: value"
    const kv = t.match(/^([^:]{2,70}):\s*(.+)$/);
    if (kv) {
      const label = kv[1].trim();
      const val = kv[2].trim();
      const num =
        (val.match(/currently at\s+([-+]?[\d.,]+%?)/i) || [])[1] ||
        (val.match(/^([-+]?[\d.,]+%?)\.?$/) || [])[1] ||
        (val.match(/^(\d+\s*(?:minutes?|mins?)(?:\s*\d+\s*(?:seconds?|secs?))?)/i) || [])[1] ||
        (val.match(/^([\d.,]+)\s+[a-z -]+?\.?$/i) || [])[1] ||
        (/^(nil|none|n\/a)\.?$/i.test(val) ? '0' : null);
      if (num != null && !/^total$/i.test(label)) {
        b.metrics.push({ label: (prefix ? prefix + ' — ' : '') + label, value: cleanNum(num) });
        continue;
      }
      b.notes.push(t);
      continue;
    }
    b.notes.push(t);
  }
  return days.filter((d): d is ParsedDay => !!d.date);
}

/** Matches a project name found in the document to an existing project (ignores case and a trailing ".com"). */
export const matchProject = <T extends Pick<Project, 'name'>>(projects: T[], n: string): T | undefined =>
  projects.find((p) => p.name.toLowerCase() === n.toLowerCase() || p.name.toLowerCase().replace(/\.com$/, '') === n.toLowerCase().replace(/\.com$/, ''));
/** Matches a person's name to a member (full name or first name, ignoring case). */
export const matchMember = <T extends Pick<Member, 'name'>>(members: T[], n: string): T | undefined =>
  members.find((m) => m.name.toLowerCase() === n.toLowerCase() || m.name.split(' ')[0].toLowerCase() === n.toLowerCase());

/** What an import would do (shown before anything is saved). */
export function summarizeImport(days: ParsedDay[], projects: Pick<Project, 'name'>[], members: Pick<Member, 'name'>[]) {
  const newProjects = new Set<string>();
  const newMembers = new Set<string>();
  let workLogs = 0;
  let projectLogs = 0;
  days.forEach((d) => {
    Object.keys(d.projects).forEach((n) => {
      if (!matchProject(projects, n)) newProjects.add(n);
    });
    [d.general, ...Object.values(d.projects)].forEach((b) =>
      b.logs.forEach((l) => {
        workLogs++;
        if (!matchMember(members, l.name)) newMembers.add(l.name);
      }),
    );
    Object.values(d.projects).forEach((b) => {
      if (b.notes.length || b.metrics.length) projectLogs++;
    });
  });
  return { days: days.length, workLogs, projectLogs, newMembers: [...newMembers], newProjects: [...newProjects] };
}
